import { nanoid } from 'nanoid';
import { KEYCLOAK_TOKEN_REFRESHED, KEYCLOAK_TOKEN_REFRESH, doWebsocketAuth } from 'app/store/users';
import { refreshJwtToken } from 'app/store/auth';
import { FOREIGN_HTTP_ERROR } from 'app/store/error/types';
import { doCommandChainStarted } from 'app/store/commandChain';
import { apiClient, extractData } from './utils.js';
import { API_FORBIDDEN, API_UNAUTHORIZED, HTTP_UNPROCESSABLE_ENTITY } from './constants';

const requestById = new Map();
export function apiMiddleware({ dispatch, getState }) {
    return (next) => (action) => {
        const {
            type,
            api,
            params,
            context,
            transformer = extractData,
            onSucceeded,
            onFailed,
            meta: orgMeta
        } = action;

        if (!api || !api.sync) {
            return next(action);
        }

        const TYPE_PROGRESS = `${type}/progress`;
        const TYPE_STARTED = `${type}/started`;
        const TYPE_SUCCEEDED = `${type}/succeeded`;
        const TYPE_CANCELED = `${type}/canceled`;
        const TYPE_TIMEDOUT = `${type}/timedout`;
        const TYPE_FAILED = `${type}/failed`;
        const TYPE_ENDED = `${type}/ended`;

        const { endpoint, _type } = api;
        const requestId = nanoid();
        const meta = { ...orgMeta, requestId };

        dispatch({ type: TYPE_STARTED, payload: params, meta });

        if (endpoint === 'fileupload') {
            params.formData.append('id', requestId);
            params.formData.append('type', type);
            dispatch(doCommandChainStarted(type, requestId));
        }

        const request = apiClient({ endpoint, _type, meta, context, ...params })
            .onProgress((value, meta) =>
                dispatch({ type: TYPE_PROGRESS, payload: value, getState, meta })
            )
            .onSuccess((data, meta) => {
                dispatch({
                    type: TYPE_SUCCEEDED,
                    params,
                    payload: transformer({ params, data }),
                    meta
                });

                if (onSucceeded) {
                    onSucceeded({ dispatch, params, data, meta, getState });
                }
            })
            .onError((errors, meta) => {
                if (
                    errors.code === HTTP_UNPROCESSABLE_ENTITY &&
                    errors?.data?._type?.endsWith('.Canceled')
                ) {
                    dispatch({ type: TYPE_CANCELED, meta });
                } else {
                    dispatch({ type: TYPE_FAILED, params, payload: errors, meta });
                    if (onFailed) {
                        onFailed({ dispatch, params, meta, getState, errors });
                    }
                }
            })
            .onForeignError((errors) => {
                dispatch({ type: FOREIGN_HTTP_ERROR, payload: errors });
            })
            .onAbort(() => {
                dispatch({ type: TYPE_CANCELED, meta });
            })
            .onUnAuth((errors) => {
                dispatch({ type: API_UNAUTHORIZED, payload: errors, meta });
            })
            .onTimeout((errors, _, data) => {
                dispatch({ type: TYPE_TIMEDOUT, payload: { ...errors, data }, params, meta });
            })
            .onFinish((meta) => {
                dispatch({ type: TYPE_ENDED, meta });
                if (requestById.has(requestId)) {
                    requestById.delete(requestId);
                }
            })
            .onForbidden((errors, meta) => {
                dispatch({ type: API_FORBIDDEN, payload: errors, meta });
            });

        let abortFn;

        refreshJwtToken({
            onSuccess: (token) => {
                if (token) {
                    dispatch({ type: KEYCLOAK_TOKEN_REFRESHED });
                    dispatch(doWebsocketAuth(token));
                }

                abortFn = request();
                requestById.set(requestId, abortFn);
            },
            onError: () => {
                dispatch({ type: KEYCLOAK_TOKEN_REFRESH.FAILED });
            }
        });

        return abortFn;
    };
}
